import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Switch, Table } from 'antd';
import { createEvaluation, deleteEvaluation, listEvaluations, updateEvaluation } from '../../api/evaluations';
import { listStudents } from '../../api/students';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard from '../../components/TableCard';
import { SearchBar } from '../../components/ToolbarControls';
import { ViewIcon, EditIcon, DeleteIcon } from '../../components/UIKit';

const normalizeBool = (v) => {
  if (v === true || v === 1 || v === '1' || v === 'true') return true;
  if (v === false || v === 0 || v === '0' || v === 'false') return false;
  return null;
};

export default function Evaluations() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [form] = Form.useForm();
  const watchedStudentId = Form.useWatch('student_id', form);

  const studentRows = useMemo(() => (Array.isArray(students) ? students : []), [students]);

  // Set of student IDs that already have an active evaluation
  const evaluatedStudentIds = useMemo(() => {
    const base = Array.isArray(rows) ? rows : [];
    return new Set(base.map((r) => String(r?.student_id ?? r?.Student?.id ?? '')).filter(Boolean));
  }, [rows]);

  const filteredStudents = useMemo(() => {
    const q = String(studentSearch || '').trim().toLowerCase();
    if (!q) return studentRows;
    return studentRows.filter((s) => {
      const u = s?.User;
      const nom = `${u?.prenom || ''} ${u?.nom || ''}`.trim().toLowerCase();
      return nom.includes(q) || String(u?.email || '').toLowerCase().includes(q) || String(s?.student_code || '').toLowerCase().includes(q);
    });
  }, [studentRows, studentSearch]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [evals, studs] = await Promise.all([listEvaluations({}), listStudents({})]);
      setRows(Array.isArray(evals) ? evals : []);
      setStudents(Array.isArray(studs) ? studs : []);
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les évaluations.'), placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [notification]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onAdd = () => { setEditing(null); form.resetFields(); setStudentSearch(''); setOpen(true); };
  const onView = (row) => setViewing(row);
  const onEdit = (row) => {
    setEditing(row); setStudentSearch('');
    form.setFieldsValue({ student_id: row?.student_id ?? row?.Student?.id, comportement_score: row?.comportement_score, vie_sociale_score: row?.vie_sociale_score, usage_substances: !!row?.usage_substances, frequence: row?.frequence, commentaire: row?.commentaire });
    setOpen(true);
  };

  const selectedStudentLabel = useMemo(() => {
    const sid = watchedStudentId;
    if (!sid) return null;
    const s = studentRows.find((x) => String(x?.id) === String(sid));
    if (!s) return `#${sid}`;
    const u = s?.User;
    const name = `${u?.prenom || ''} ${u?.nom || ''}`.trim();
    return name ? `${name} (${u?.email || ''})` : u?.email || `#${s.id}`;
  }, [studentRows, watchedStudentId]);

  const openStudentPicker = () => { setStudentSearch(''); setSelectedStudentId(form.getFieldValue('student_id') ? Number(form.getFieldValue('student_id')) : null); setPickerOpen(true); };
  const onConfirmPickStudent = () => { if (!selectedStudentId) return; form.setFieldsValue({ student_id: selectedStudentId }); setPickerOpen(false); setStudentSearch(''); };

  const onDelete = async (row) => {
    try { await deleteEvaluation(row.id); message.success('Évaluation supprimée.'); fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression échouée.'), placement: 'topRight' }); }
  };

  const onSubmit = async () => {
    try {
      const v = await form.validateFields();
      // Block duplicate evaluation on create
      if (!editing?.id) {
        const sid = String(v.student_id ?? '');
        if (evaluatedStudentIds.has(sid)) {
          const s = studentRows.find((x) => String(x?.id) === sid);
          const name = s ? `${s?.User?.prenom || ''} ${s?.User?.nom || ''}`.trim() || s?.User?.email : `#${sid}`;
          notification.error({ message: `Évaluation existante`, description: `${name} a déjà une évaluation active. Modifiez-la ou supprimez-la avant d'en créer une nouvelle.`, placement: 'topRight', duration: 6 });
          return;
        }
      }
      const payload = { student_id: v.student_id, comportement_score: v.comportement_score, vie_sociale_score: v.vie_sociale_score, usage_substances: v.usage_substances === true ? true : v.usage_substances === false ? false : null, frequence: v.frequence || null, commentaire: v.commentaire || null };
      if (editing?.id) { await updateEvaluation(editing.id, payload); message.success('Évaluation mise à jour.'); }
      else { await createEvaluation(payload); message.success('Évaluation enregistrée.'); }
      setOpen(false); setEditing(null); fetchAll();
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Enregistrement échoué.'), placement: 'topRight' }); }
  };

  const filteredRows = useMemo(() => {
    const q = String(tableSearch || '').trim().toLowerCase();
    const base = Array.isArray(rows) ? rows : [];
    if (!q) return base;
    return base.filter((r) => {
      const code = String(r?.Student?.student_code || '').toLowerCase();
      const nom = `${r?.Student?.User?.prenom || ''} ${r?.Student?.User?.nom || ''}`.toLowerCase();
      const email = String(r?.Student?.User?.email || '').toLowerCase();
      return code.includes(q) || nom.includes(q) || email.includes(q);
    });
  }, [rows, tableSearch]);

  return (
    <div className="page">
      <TableCard
        label="Évaluations"
        loading={loading}
        rows={filteredRows}
        toolbar={<button className="btn-primary" onClick={onAdd}>+ Nouvelle évaluation</button>}
        search={<SearchBar value={tableSearch} onChange={setTableSearch} placeholder="Rechercher un étudiant (ID, nom, email)" />}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>ID Étudiant</th>
              <th>Étudiant</th>
              <th>Email</th>
              <th>Comportement</th>
              <th>Vie sociale</th>
              <th>Substances</th>
              <th>Fréquence</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                <td>{r.Student?.student_code || '-'}</td>
                <td>{`${r.Student?.User?.prenom || ''} ${r.Student?.User?.nom || ''}`.trim() || '-'}</td>
                <td>{r.Student?.User?.email || '-'}</td>
                <td>{r.comportement_score ?? '-'}</td>
                <td>{r.vie_sociale_score ?? '-'}</td>
                <td>{normalizeBool(r.usage_substances) === true ? 'Oui' : normalizeBool(r.usage_substances) === false ? 'Non' : '-'}</td>
                <td>{r.frequence || '-'}</td>
                <td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="action-btn" title="Voir" onClick={() => onView(r)}><ViewIcon /></button>
                    <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}><EditIcon /></button>
                    <Popconfirm title="Supprimer cette évaluation ?" okText="Supprimer" cancelText="Annuler" onConfirm={() => onDelete(r)}>
                      <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                    </Popconfirm>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* Form Drawer */}
      <Drawer title={editing ? 'Modifier une évaluation' : 'Nouvelle évaluation'} placement="right" width={480} onClose={() => { setOpen(false); setEditing(null); }} open={open} destroyOnClose extra={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => { setOpen(false); setEditing(null); }}>Annuler</button><button className="btn-primary" onClick={onSubmit}>Enregistrer</button></div>}>
        <Form form={form} layout="vertical">
          <Form.Item name="student_id" rules={[{ required: true }]} hidden><Input /></Form.Item>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <button type="button" className="btn-outline" onClick={openStudentPicker}>Choisir un étudiant</button>
              {watchedStudentId && <button type="button" className="btn-outline" onClick={() => { form.setFieldsValue({ student_id: null }); setSelectedStudentId(null); }}>Effacer</button>}
            </div>
            <p style={{ fontSize: 12, color: selectedStudentLabel ? '#1a1a1a' : '#aaa' }}>{selectedStudentLabel || 'Aucun étudiant sélectionné'}</p>
          </div>
          <Form.Item name="comportement_score" label="Score comportement (0-10)"><InputNumber min={0} max={10} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="vie_sociale_score" label="Score vie sociale (0-10)"><InputNumber min={0} max={10} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="usage_substances" label="Substances" valuePropName="checked"><Switch checkedChildren="Oui" unCheckedChildren="Non" /></Form.Item>
          <Form.Item name="frequence" label="Fréquence"><Input /></Form.Item>
          <Form.Item name="commentaire" label="Commentaire"><Input.TextArea rows={4} /></Form.Item>
        </Form>
      </Drawer>

      {/* Student picker modal */}
      <Modal title="Choisir un étudiant" open={pickerOpen} onCancel={() => { setPickerOpen(false); setStudentSearch(''); }} onOk={onConfirmPickStudent} okText="Sélectionner" okButtonProps={{ disabled: !selectedStudentId || (evaluatedStudentIds.has(String(selectedStudentId)) && !editing) }} width={800} centered zIndex={2000}>
        <Input placeholder="Rechercher un étudiant (ID, nom, email)" value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} style={{ marginBottom: 12 }} />
        <Table size="small" rowKey="id" dataSource={filteredStudents} pagination={{ hideOnSinglePage: true, pageSize: 8, size: 'small' }}
          rowSelection={{ type: 'radio', selectedRowKeys: selectedStudentId ? [selectedStudentId] : [], onChange: (keys) => setSelectedStudentId(keys?.[0] ?? null),
            getCheckboxProps: (record) => ({ disabled: !editing && evaluatedStudentIds.has(String(record.id)) })
          }}
          onRow={(record) => ({ onClick: () => { if (!editing && evaluatedStudentIds.has(String(record.id))) return; setSelectedStudentId(record.id); } })}
          columns={[
            { title: 'ID', dataIndex: 'student_code', width: 120 },
            { title: 'Nom', render: (_, s) => `${s?.User?.prenom || ''} ${s?.User?.nom || ''}`.trim() || '-' },
            { title: 'Email', render: (_, s) => s?.User?.email || '-' },
            { title: '', width: 110, render: (_, s) => evaluatedStudentIds.has(String(s.id)) ? <span className="tag tag-amber">Déjà évalué</span> : null },
          ]} />
      </Modal>

      {/* View modal */}
      <Modal title="Détail évaluation" open={!!viewing} onCancel={() => setViewing(null)} footer={<button className="btn-outline" onClick={() => setViewing(null)}>Fermer</button>}>
        {viewing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
            {[
              ['Étudiant', `${viewing?.Student?.User?.prenom || ''} ${viewing?.Student?.User?.nom || ''}`.trim()],
              ['Email', viewing?.Student?.User?.email],
              ['Comportement', viewing?.comportement_score ?? '-'],
              ['Vie sociale', viewing?.vie_sociale_score ?? '-'],
              ['Substances', normalizeBool(viewing?.usage_substances) === true ? 'Oui' : normalizeBool(viewing?.usage_substances) === false ? 'Non' : '-'],
              ['Fréquence', viewing?.frequence || '-'],
            ].map(([label, val]) => (
              <div key={label}><strong style={{ color: '#888', letterSpacing: '0.05em', fontSize: 10, textTransform: 'uppercase' }}>{label}</strong><div style={{ marginTop: 2 }}>{val || '-'}</div></div>
            ))}
            <div><strong style={{ color: '#888', letterSpacing: '0.05em', fontSize: 10, textTransform: 'uppercase' }}>Commentaire</strong><div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>{viewing?.commentaire || '-'}</div></div>
          </div>
        )}
      </Modal>
    </div>
  );
}

